import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChatThreadContext } from '@/components/common/chat/chatThreadContext';
import { useGT } from 'gt-react';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState, type Message } from '@/context/agent-state.context';
import { sharesThread } from '@/context/sharesThread';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { NamiPanelHeader } from '@/components/nami/NamiPanelHeader';
import { NamiSuggestionChips } from '@/components/nami/NamiSuggestionChips';
import { NamiRecommendedActions } from '@/components/nami/NamiRecommendedActions';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanelEnabled } from './useAgentPanelEnabled';
import { ChatMessageList } from '@/components/common/chat/ChatMessageList';
import { ChatInput } from '@/components/common/chat/ChatInput';
import {
  ChatFileDropSurface,
  ChatFileInput,
} from '@/components/common/chat/ChatFileDropSurface';
import { useChatAttachments } from '@/components/common/chat/useChatAttachments';
import { QueuedMessages } from '@/components/common/chat/QueuedMessages';
import { canSubmitMessage } from '@/components/common/chat/composerRules';
import { AnnotationToolbar } from '@/components/common/chat/AnnotationToolbar';
import { useCaptureScreenHandler } from '@/hooks/useCaptureScreenHandler';
import {
  AgentOverlay,
  clearOverlay,
} from '@/components/common/chat/AgentOverlay';
import { useAnnotationCanvas } from '@/context/annotation-canvas.context';
import { captureScreen } from '@/utils/capture-screen';
import type { MessageChannel, QueueMode } from '@erp/rpc';
import {
  TodoListPanel,
  useTodoList,
  useTodoListTools,
} from '@/components/common/chat/todo-list';
import { ApiCallApprovalPanel } from '@/components/common/chat/api-call-approval/ApiCallApprovalPanel';
import {
  ForkMessageContext,
  useForkFromMessage,
} from '@/components/common/chat/useForkFromMessage';
import { useNavigationTools } from '@/components/nami/useNavigationTools';
import {
  VoiceCallButton,
  VoiceCallWaveform,
} from '@/components/common/chat/voice-call/VoiceCallButton';
import { useChatVoiceCall } from '@/components/common/chat/voice-call/useChatVoiceCall';
import { withLiveCall } from '@/components/common/chat/voice-call/callTurns';
import { useNoteSharedOnCall } from '@/components/common/chat/voice-call/useNoteSharedOnCall';
import { useAgentReplyWaiter } from '@/components/common/chat/voice-call/agentReply';

/** The message and tool result of the first open yield, pinned above the input. */
function stickyYieldOf(
  activeYields: readonly { toolCallId: string }[],
  messages: readonly Message[],
) {
  const yield_ = activeYields[0];
  if (!yield_) return null;
  for (const msg of messages) {
    if (msg.role !== 'assistant' || !msg.toolResults) continue;
    const tr = msg.toolResults.find((t) => t.toolCallId === yield_.toolCallId);
    if (tr) return { msg, tr };
  }
  return null;
}

/**
 * Global chat agent renderer. Mounted once in the authenticated layout.
 * Portals chat UI into whatever AgentContainer is currently registered.
 */
// fallow-ignore-next-line complexity -- pre-existing 370-line panel; AI-518 adds only the queue wiring, with its rules in composerRules.
export function ChatAgentRenderer() {
  const gt = useGT();
  const {
    containerElement,
    moduleConfig: pageModuleConfig,
    effectiveModuleConfig: moduleConfig,
    getEffectiveModuleConfig,
  } = useAgentContainer();
  const enabled = useAgentPanelEnabled(moduleConfig);
  const { enabled: fullChatEnabled } = useFeatureFlag(
    'chat-agent-enabled',
    false,
  );
  const { isOpen, close } = useAgentPanel();
  const {
    isLoading,
    isCapturingScreen,
    messages,
    sendMessage,
    recordVoiceTurn,
    streamingText,
    activeToolCalls,
    cancel,
    reset,
    error,
    currentThreadId,
    currentModuleKey,
    activeYields,
    queuedMessages,
    withdrawQueued,
    canQueueFor,
    setLiveFrontendTools,
  } = useAgentState();
  // While a reply streams on this page's thread, a message queues behind it
  // (AI-518). A screenshot can't be captured mid-reply.
  const canQueue = moduleConfig ? canQueueFor(moduleConfig.moduleKey) : false;
  const onForkMessage = useForkFromMessage();
  const { clearTodoList } = useTodoList();
  const todoListTools = useTodoListTools();
  const navigationTools = useNavigationTools();
  // A tab reloaded mid-turn has no send to carry its tools, so the state
  // context runs the turn's pending page tools from this registration.
  const pageTools = moduleConfig?.tools;
  const liveModuleKey = moduleConfig?.moduleKey ?? null;
  const liveScope = moduleConfig?.scope ?? null;
  useEffect(() => {
    // Only the page module's own tools may replay on reattach. Todo and
    // navigation tools share the page's scope but are not idempotent (a lost
    // result would re-run go_back), so they are never registered here.
    setLiveFrontendTools(pageTools ?? [], liveModuleKey, liveScope);
    // Leaving the page drops its handlers and scope, so a pending page tool
    // call waits for the page that owns it instead of a stale handler.
    return () => setLiveFrontendTools([], null);
  }, [setLiveFrontendTools, pageTools, liveModuleKey, liveScope]);
  const [inputValue, setInputValue] = useState('');
  const [annotationCount, setAnnotationCount] = useState(0);
  const { isActive: isAnnotating, setActive, clear } = useAnnotationCanvas();
  const attachments = useChatAttachments(currentThreadId);

  const prevModuleKeyRef = useRef(currentModuleKey);
  useEffect(() => {
    const prev = prevModuleKeyRef.current;
    if (
      prev !== null &&
      currentModuleKey !== null &&
      !sharesThread(prev, currentModuleKey)
    ) {
      clearTodoList();
    }
    prevModuleKeyRef.current = currentModuleKey;
  }, [currentModuleKey, clearTodoList]);

  useCaptureScreenHandler();

  const handleInteractiveModeToggle = () => {
    if (isAnnotating) {
      setActive(false);
      clear();
    } else {
      setActive(true);
    }
  };

  const agentOverlay = createPortal(
    <AgentOverlay onCountChange={setAnnotationCount} />,
    document.body,
  );

  /** Sends the message; false when it could not be sent right now. */
  const handleSend = (
    overrideText?: string,
    queueMode?: QueueMode,
    channel?: MessageChannel,
  ): boolean => {
    const trimmed = (overrideText ?? inputValue).trim();
    if (
      !canSubmitMessage({
        hasText: !!trimmed,
        // A spoken request can't wait for a reply to end: its screen
        // capture is taken now and queues with it.
        isAnnotating: isAnnotating && channel !== 'voice',
        isLoading,
        canQueue,
        uploading: attachments.uploading,
      })
    ) {
      return false;
    }
    const cfg = getEffectiveModuleConfig();
    if (!cfg) return false;
    const wasAnnotating = isAnnotating;
    const moduleContext = {
      ...cfg.getModuleContext?.(),
      interactiveModeActive: wasAnnotating,
    };

    const taken = attachments.takeReady();

    // A spoken request leaves a draft typed before the call alone.
    if (channel !== 'voice') setInputValue('');
    setActive(false);

    const allTools = [
      ...(cfg.tools ?? []),
      ...todoListTools,
      ...navigationTools,
    ];

    const capturePromise = wasAnnotating
      ? captureScreen().then((captured) => {
          clear();
          return { imageData: captured.imageData, mimeType: captured.mimeType };
        })
      : undefined;
    void sendMessage(
      trimmed,
      cfg.moduleKey,
      cfg.currentPage,
      moduleContext,
      cfg.pageKey,
      allTools,
      capturePromise,
      taken.attachments,
      queueMode,
      channel,
    ).then((outcome) => {
      // Refused, not sent: offer the text again unless a new draft started,
      // and its files alongside any added since.
      if (outcome !== 'rejected') return;
      if (channel !== 'voice') setInputValue((current) => current || trimmed);
      taken.restore();
    });
    return true;
  };

  const waitForReply = useAgentReplyWaiter({ messages, isLoading, error });

  /**
   * One voice-call request, sent like a typed message so pending attachments
   * and a screen capture go with it.
   */
  const handleVoiceRequest = (request: string): Promise<string> => {
    if (!handleSend(request, undefined, 'voice')) {
      return Promise.reject(
        new Error('The assistant cannot take a request now'),
      );
    }
    return waitForReply(request);
  };

  /** A spoken exchange the assistant answered itself, saved to the thread. */
  const handleVoiceTurn = (role: 'user' | 'assistant', text: string) => {
    const cfg = getEffectiveModuleConfig();
    if (cfg) recordVoiceTurn(role, text, cfg.moduleKey);
  };

  const call = useChatVoiceCall(
    handleVoiceRequest,
    handleVoiceTurn,
    enabled && isOpen,
  );
  const inCall = call.status !== 'idle';
  useNoteSharedOnCall(call, {
    screen: isAnnotating,
    files: attachments.pending.filter((att) => !att.uploading).length,
  });
  const threadMessages = withLiveCall(messages, call.live);

  const stickyYieldData = moduleConfig?.renderToolResult
    ? stickyYieldOf(activeYields, messages)
    : null;
  const excludeToolResultIds = stickyYieldData
    ? new Set([stickyYieldData.tr.toolCallId])
    : undefined;

  if (!enabled || !containerElement || !moduleConfig) return null;
  if (!isOpen) return null;

  const placeholder = moduleConfig.placeholder;
  // The context line: the page's own surface title (plus its record), or, on
  // the layout-level `home` fallback, a hint that Nami is not page-bound here.
  const usingDefaultSurface = pageModuleConfig === null;
  const subtitle = moduleConfig.surfaceTitle
    ? moduleConfig.entity
      ? `${moduleConfig.surfaceTitle} · ${moduleConfig.entity.label}`
      : moduleConfig.surfaceTitle
    : usingDefaultSurface
      ? gt('Ask about anything in the app', {
          context:
            'context line in the AI assistant panel header when no page-specific assistant is registered',
        })
      : undefined;
  // The full-page chat continues the same thread only for the shared family.
  const showFullChatLink =
    fullChatEnabled &&
    (currentModuleKey === null || sharesThread(currentModuleKey, 'home'));
  const suggestions = moduleConfig.suggestions ?? [];
  const threadEmpty = threadMessages.length === 0 && !isLoading;
  const showSuggestions = suggestions.length > 0 && threadEmpty;

  return (
    <>
      {agentOverlay}
      {createPortal(
        <ChatFileDropSurface
          className="flex h-full min-h-0 w-full flex-1 flex-col"
          onDropFiles={(files) => {
            void attachments.uploadFiles(files);
          }}
        >
          <NamiPanelHeader
            icon={moduleConfig.headerIcon}
            title={moduleConfig.headerTitle}
            subtitle={subtitle}
            onClear={() => {
              reset();
              clearTodoList();
            }}
            clearDisabled={isLoading || threadMessages.length === 0}
            onClose={close}
            showFullChatLink={showFullChatLink}
          />

          {annotationCount > 0 && (
            <button
              type="button"
              onClick={clearOverlay}
              className="flex items-center justify-between w-full px-3 py-1.5 bg-muted border-b text-xs text-foreground hover:bg-accent transition-colors"
            >
              <span>
                {gt('Annotations visible on screen', {
                  context:
                    'chat agent banner when screen annotations overlay is visible',
                })}
              </span>
              <span className="font-medium">
                {gt('Dismiss', {
                  context:
                    'chat agent banner control to dismiss the annotations overlay',
                })}
              </span>
            </button>
          )}

          <TodoListPanel />
          <ApiCallApprovalPanel />

          <ForkMessageContext value={onForkMessage}>
            <ChatThreadContext value={currentThreadId}>
              <ChatMessageList
                messages={threadMessages}
                streamingText={streamingText}
                isLoading={isLoading}
                isCapturingScreen={isCapturingScreen}
                error={error}
                activeToolCalls={activeToolCalls}
                messageMaxWidth="max-w-[85%]"
                renderToolResult={moduleConfig.renderToolResult}
                excludeToolResultIds={excludeToolResultIds}
                onFocusNodes={moduleConfig.onFocusNodes}
                spokenLead={call.live.filler}
              />
            </ChatThreadContext>
          </ForkMessageContext>

          {stickyYieldData && moduleConfig.renderToolResult && (
            <div className="px-4 py-2 border-t bg-background flex-shrink-0">
              {moduleConfig.renderToolResult(
                stickyYieldData.msg,
                stickyYieldData.tr,
              )}
            </div>
          )}

          <div className="p-3 border-t flex-shrink-0">
            <ChatFileInput
              inputRef={attachments.fileInputRef}
              onChange={attachments.onFileInputChange}
            />
            {moduleConfig.inputNotice && (
              <p className="mb-2 text-xs text-muted-foreground">
                {moduleConfig.inputNotice}
              </p>
            )}
            {threadEmpty && moduleConfig.pageKey && (
              <NamiRecommendedActions
                pageKey={moduleConfig.pageKey}
                entityId={moduleConfig.entity?.id}
                onSelect={(prompt) => handleSend(prompt)}
                disabled={isLoading}
              />
            )}
            {showSuggestions && (
              <NamiSuggestionChips
                suggestions={suggestions}
                onSelect={(text) => handleSend(text)}
                disabled={isLoading}
              />
            )}
            <QueuedMessages
              messages={queuedMessages}
              onWithdraw={withdrawQueued}
            />
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              queueWhileLoading={canQueue}
              onSteer={() => handleSend(undefined, 'steer')}
              isLoading={isLoading}
              onCancel={cancel}
              placeholder={placeholder}
              autoFocus={isOpen}
              leftSlot={<VoiceCallButton call={call} />}
              inputReplacement={
                inCall ? <VoiceCallWaveform call={call} /> : undefined
              }
              enableVoiceInput={!inCall}
              hideSendAction={inCall}
              onVoiceTranscription={(text) => setInputValue(text ?? '')}
              onShareToggle={handleInteractiveModeToggle}
              isShareActive={isAnnotating}
              annotationToolbar={<AnnotationToolbar />}
              attachments={attachments.pending}
              onAttachFile={attachments.openFilePicker}
              onRemoveAttachment={attachments.remove}
              sendDisabled={attachments.uploading}
            />
          </div>
        </ChatFileDropSurface>,
        containerElement,
      )}
    </>
  );
}
